import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Caution, Dialog, Field, SECONDARY_BUTTON } from '@/features/inventory/registerUi';
import {
  classifyClearError,
  useClearDevice,
  useClearanceCandidates,
  type ClearanceCandidate,
} from './useClearance';

/**
 * SLICE 14d — CLEAR A DEVICE FOR COMMANDING, FROM THE CLEARANCES SCREEN.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS: the screen named after clearances was a read-only LIST. The
 * action that MAKES a clearance lived only on the commissioning bench, so the
 * screen showed "0 devices" with no way to make one. This is the missing verb.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * It is the bench's `ClearForCommandingDialog` with a PICKER in front: the
 * operator is not on a device row here, so they choose the device first. Each
 * option reads as something they recognise — PLATE first, then tenant, then IMEI —
 * and `plate === null` is warehouse stock, labelled, never a blank.
 *
 * 🔴 SAME ATTESTATION, SAME NOTE FLOOR, SAME MUTATION. It reuses `useClearDevice`
 * and `classifyClearError` — there is one write path, not two — and enforces the
 * 10-character note client-side with the server 422 as the belt to the
 * suspenders. The note is the whole worth of the record; the hint says so.
 */
const NOTE =
  'w-full min-h-[5rem] rounded-lg border border-raad-border bg-raad-input px-3 py-2 text-[13.5px] text-raad-text outline-none focus:border-raad-primary';
const SELECT =
  'w-full min-h-10 rounded-lg border border-raad-border bg-raad-input px-3 text-[13.5px] text-raad-text outline-none focus:border-raad-primary';

/** PLATE first (what an operator recognises), then tenant, then IMEI. Warehouse
 *  stock has no plate — labelled, not left blank. */
function candidateLabel(c: ClearanceCandidate, stockLabel: string): string {
  const head = c.plate ?? stockLabel;
  const parts = [head];
  if (c.tenantName) parts.push(c.tenantName);
  parts.push(c.imei);
  return parts.join(' · ');
}

export function ClearDevicePickerDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const candidatesQ = useClearanceCandidates(true);
  const clear = useClearDevice();
  const [deviceId, setDeviceId] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const candidates = Array.isArray(candidatesQ.data?.candidates)
    ? candidatesQ.data.candidates
    : [];
  const chosen = candidates.find((c) => c.deviceId === deviceId) ?? null;

  // Mirrors the server floor (min 10 after trim), AND a device must be chosen.
  const ready = deviceId !== '' && note.trim().length >= 10;

  async function submit() {
    if (!ready) return;
    setError(null);
    try {
      await clear.mutateAsync({ deviceId, note: note.trim() });
      setDone(true);
    } catch (e) {
      setError(t(`clearance.clear.error.${classifyClearError(e)}`));
    }
  }

  return (
    <Dialog title={t('clearance.pick.title')} onClose={onClose} wide>
      {done && chosen ? (
        <div className="grid gap-3">
          <p className="rounded-lg border border-raad-warn/40 bg-raad-warn/10 p-3 text-[12.5px] text-raad-text">
            {t('clearance.clear.done', { imei: chosen.imei })}
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary"
            >
              {t('clearance.clear.doneButton')}
            </button>
          </div>
        </div>
      ) : candidatesQ.isPending ? (
        <p className="text-sm text-raad-muted" role="status">
          {t('clearance.pick.loading')}
        </p>
      ) : candidatesQ.isError ? (
        <p role="alert" className="text-sm text-raad-error">
          {t('clearance.pick.error')}
        </p>
      ) : candidates.length === 0 ? (
        <p className="text-sm text-raad-muted">{t('clearance.pick.none')}</p>
      ) : (
        <div className="grid gap-3">
          <p className="text-[13px]">{t('clearance.clear.intro')}</p>
          <Caution>{t('clearance.clear.smsCaveat')}</Caution>

          <Field label={t('clearance.pick.deviceLabel')} hint={t('clearance.pick.deviceHint')}>
            <select
              className={SELECT}
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              autoFocus
            >
              <option value="">{t('clearance.pick.placeholder')}</option>
              {candidates.map((c) => (
                <option key={c.deviceId} value={c.deviceId}>
                  {candidateLabel(c, t('clearance.pick.stock'))}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t('clearance.clear.noteLabel')} hint={t('clearance.clear.noteHint')}>
            <textarea
              className={NOTE}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
          </Field>

          {error ? (
            <p
              role="alert"
              className="rounded-lg border border-raad-error/40 bg-raad-error/10 p-3 text-[12.5px] text-raad-text"
            >
              {error}
            </p>
          ) : null}

          <div className="mt-2 flex items-center justify-end gap-3">
            <button type="button" className={SECONDARY_BUTTON} onClick={onClose}>
              {t('inventory.close')}
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={!ready || clear.isPending}
              className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary disabled:opacity-40"
            >
              {clear.isPending ? t('clearance.clear.working') : t('clearance.clear.submit')}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
